import { type Atom, atom, type Getter, type Setter, type WritableAtom } from 'jotai';
import { jotaiStore } from './store';

const DEFAULT_DURATION = 6000;

const PERSISTENT_SEVERITIES: AlertSnackbar['alertSeverity'][] = ['error'];

export type Banner = {
  /**
   * The id of the banner. If not provided, a random id will be generated.
   * When passed, the banner will be updated if it already exists.
   */
  id?: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  action?: { label: string; onClick: () => void };
};

export type PrimarySnackbar = {
  /**
   * The id of the snackbar. If not provided, a random id will be generated.
   * When passed, the snackbar will be updated if it already exists.
   */
  id?: string;
  message: string;
  /**
   * The duration of the snackbar in milliseconds.
   * If not provided, the snackbar will be removed after 6000ms.
   */
  duration?: number;
};

export type AlertSnackbar = {
  /**
   * The id of the snackbar. If not provided, a random id will be generated.
   * When passed, the snackbar will be updated if it already exists.
   */
  id?: string;
  message: string | React.ReactNode | ((params: { close: () => void }) => React.ReactNode);
  alertSeverity: 'info' | 'warning' | 'error' | 'success';
  /**
   * An action to display in the snackbar.
   *
   * @example
   * ```ts
   * import { RefreshCcwIcon } from 'lucide-react';
   * showAlertSnackbar({
   *   message: 'Alert with action',
   *   alertSeverity: 'warning',
   *   action: {
   *     label: 'Retry',
   *     onClick: () => console.log('Retry'),
   *     icon: <RefreshCcwIcon />,
   *   },
   * });
   */
  action?: {
    /** The text to display on the action button. */
    label: string;
    /** The function to call when the action button is clicked. */
    onClick: (params: { close: () => void }) => void;
    /** An optional icon to display before the label. */
    icon?: React.ReactNode;
  };
  /**
   * The duration of the snackbar in milliseconds.
   * If not provided, the snackbar will be removed after 6000ms.
   */
  duration?: number;
};

export type ProcessedBanner = Banner & {
  id: string;
};

export type ProcessedPrimarySnackbar = PrimarySnackbar & {
  id: string;
  type: 'primary';
  activatedAt?: number;
};

export type ProcessedAlertSnackbar = AlertSnackbar & {
  id: string;
  type: 'alert';
  activatedAt?: number;
};

type AnySnackbar = {
  id?: string;
  duration?: number;
  message: string | React.ReactNode | ((params: { close: () => void }) => React.ReactNode);
};

export type ProcessedSnackbar = ProcessedPrimarySnackbar | ProcessedAlertSnackbar;

/**
 * Show a banner notification
 *
 * This should be used in React components to show a banner notification.
 * For other use cases, use the `showBanner` function.
 *
 * @example
 * ```ts
 * const showBanner = useSetAtom(showBannerAtom);
 * showBanner({ message: 'System maintenance scheduled for tonight', type: 'warning' });
 * ```
 */
export const showBannerAtom = atom(null, (get, set, banner: Banner) => {
  const current = get(bannersAtom);

  // Generate ID if not provided
  const id = banner.id || Date.now().toString() + Math.random().toString(36);

  const processedBanner: ProcessedBanner = {
    ...banner,
    id,
  };

  // Check if banner with this ID already exists
  const existingIndex = current.findIndex((b) => b.id === id);

  if (existingIndex >= 0) {
    // Update existing banner
    const updated = [...current];
    updated[existingIndex] = processedBanner;
    set(bannersAtom, updated);
  } else {
    // Add new banner
    set(bannersAtom, [...current, processedBanner]);
  }
});

/**
 * Show a primary snackbar notification
 *
 * This should be used in React components to show a primary snackbar notification.
 * For other use cases, use the `showPrimarySnackbar` function.
 *
 * @example
 * ```ts
 * const showPrimarySnackbar = useSetAtom(showPrimarySnackbarAtom);
 * showPrimarySnackbar({ message: 'Data saved successfully' });
 * ```
 */
export const showPrimarySnackbarAtom = atom(null, (get, set, snackbar: PrimarySnackbar) => {
  manageQueue(
    get,
    set,
    snackbar,
    primarySnackbarsAtom,
    currentPrimarySnackbarAtom,
    primaryTimerAtom,
    removePrimarySnackbarAtom,
    'primary'
  );
});

/**
 * Show an alert snackbar notification
 *
 * This should be used in React components to show an alert snackbar notification.
 * For other use cases, use the `showAlertSnackbar` function.
 *
 * @example
 * ```ts
 * const showAlertSnackbar = useSetAtom(showAlertSnackbarAtom);
 * showAlertSnackbar({ message: 'Failed to connect to the server.', alertSeverity: 'error' });
 * ```
 */
export const showAlertSnackbarAtom = atom(null, (get, set, snackbar: AlertSnackbar) => {
  manageQueue(
    get,
    set,
    snackbar,
    alertSnackbarsAtom,
    currentAlertSnackbarAtom,
    alertTimerAtom,
    removeAlertSnackbarAtom,
    'alert'
  );
});

/**
 * Show a banner notification
 *
 * This can be used outside of React components, for example in Redux actions.
 *
 * @example
 * ```ts
 * showBanner({ message: 'System maintenance scheduled for tonight', type: 'warning' });
 * ```
 */
export const showBanner = (banner: Banner) => {
  jotaiStore.set(showBannerAtom, banner);
};

/**
 * Show a primary snackbar notification
 *
 * This can be used outside of React components, for example in Redux actions.
 *
 * @example
 * ```ts
 * showPrimarySnackbar({ message: 'Data saved successfully' });
 * ```
 */
export const showPrimarySnackbar = (snackbar: PrimarySnackbar) => {
  jotaiStore.set(showPrimarySnackbarAtom, snackbar);
};

/**
 * Show an alert snackbar notification
 *
 * This can be used outside of React components, for example in Redux actions.
 *
 * @example
 * ```ts
 * showAlertSnackbar({ message: 'Failed to connect to the server.', alertSeverity: 'error' });
 * ```
 */
export const showAlertSnackbar = (snackbar: AlertSnackbar) => {
  jotaiStore.set(showAlertSnackbarAtom, snackbar);
};

// Banner atoms
export const bannersAtom = atom<ProcessedBanner[]>([]);

export const removeBannerAtom = atom(null, (get, set, id: string) => {
  const current = get(bannersAtom);
  set(
    bannersAtom,
    current.filter((b) => b.id !== id)
  );
});

// Snackbar atoms - separate queues for each type
export const primarySnackbarsAtom = atom<ProcessedPrimarySnackbar[]>([]);
export const alertSnackbarsAtom = atom<ProcessedAlertSnackbar[]>([]);

export const currentPrimarySnackbarAtom = atom((get) => {
  const snackbars = get(primarySnackbarsAtom);
  return snackbars.length > 0 ? snackbars[0] : null;
});

export const currentAlertSnackbarAtom = atom((get) => {
  const snackbars = get(alertSnackbarsAtom);
  return snackbars.length > 0 ? snackbars[0] : null;
});

const primaryTimerAtom = atom<NodeJS.Timeout | null>(null);
const alertTimerAtom = atom<NodeJS.Timeout | null>(null);
type TimerAtom = typeof primaryTimerAtom;

const manageTimer = (
  get: Getter,
  set: Setter,
  timerAtom: TimerAtom,
  removeAtom: RemoveSnackbarAtom,
  currentSnackbar: ProcessedSnackbar | null,
  // queueAtom is either primarySnackbarsAtom or alertSnackbarsAtom
  queueAtom: WritableAtom<ProcessedSnackbar[], any, void>
) => {
  // Clear existing timer
  const currentTimer: NodeJS.Timeout | null = get(timerAtom);
  if (currentTimer) {
    clearTimeout(currentTimer);
    set(timerAtom, null);
  }

  if (!currentSnackbar) {
    return;
  }

  // If this is the first time we're timing this snackbar, record its activation time.
  // This is crucial for calculating remaining duration later.
  let snackbarToTime = { ...currentSnackbar };
  if (!snackbarToTime.activatedAt) {
    snackbarToTime = { ...snackbarToTime, activatedAt: Date.now() };
    // Persist this back to the atom state so it's available in subsequent calls
    const queue = get(queueAtom);
    if (queue.length > 0 && queue[0]?.id === snackbarToTime.id) {
      const newQueue = [...queue];
      newQueue[0] = snackbarToTime;
      set(queueAtom, newQueue);
    }
  }

  const queue = get(queueAtom);
  // For AlertSnackbars with severity other than 'success', don't set a timer if it's the only one in the queue.
  if (
    snackbarToTime.type === 'alert' &&
    PERSISTENT_SEVERITIES.includes(snackbarToTime.alertSeverity)
  ) {
    if (queue.length <= 1) {
      return; // Persist until manually closed or another snackbar is added
    }
  }

  const totalDuration = snackbarToTime.duration ?? DEFAULT_DURATION;
  const elapsed = Date.now() - (snackbarToTime.activatedAt || Date.now());
  const remainingDuration = Math.max(0, totalDuration - elapsed);

  const timer = setTimeout(() => {
    if (snackbarToTime.id) {
      set(removeAtom, snackbarToTime.id);
    }
  }, remainingDuration);
  set(timerAtom, timer);
};

// Generic queue management logic
function manageQueue<
  TInput extends AnySnackbar,
  TProcessed extends ProcessedSnackbar,
  TQueueAtom extends WritableAtom<TProcessed[], any, void>,
  TCurrentAtom extends Atom<TProcessed | null | undefined>,
  TTimerAtom extends TimerAtom,
  TRemoveAtom extends RemoveSnackbarAtom,
>(
  get: Getter,
  set: Setter,
  snackbar: TInput,
  queueAtom: TQueueAtom,
  currentAtom: TCurrentAtom,
  timerAtom: TTimerAtom,
  removeAtom: TRemoveAtom,
  type: string
) {
  const current = get(queueAtom);

  // Generate ID if not provided
  const id = snackbar.id || Date.now().toString() + Math.random().toString(36);

  const processedSnackbar = {
    ...snackbar,
    id,
    type,
  } as unknown as TProcessed;

  // Check if snackbar with this ID already exists
  const existingIndex = current.findIndex((n) => n.id === id);

  if (existingIndex >= 0) {
    // Update existing snackbar
    const updated = [...current];
    updated[existingIndex] = processedSnackbar;
    set(queueAtom, updated);
  } else {
    // Add new snackbar to the queue
    set(queueAtom, [...current, processedSnackbar]);
  }

  // If a snackbar is added or updated, we need to re-evaluate the timer for the current snackbar.
  const currentAfter = get(currentAtom);
  manageTimer(get, set, timerAtom, removeAtom, currentAfter || null, queueAtom);
}

// Create base remove atoms without timer management
const basePrimaryRemoveAtom = atom(null, (get, set, id: string) => {
  const current = get(primarySnackbarsAtom);
  set(
    primarySnackbarsAtom,
    current.filter((n) => n.id !== id)
  );
});

const baseAlertRemoveAtom = atom(null, (get, set, id: string) => {
  const current = get(alertSnackbarsAtom);
  set(
    alertSnackbarsAtom,
    current.filter((n) => n.id !== id)
  );
});

// Timer continuation atoms - these handle progressing to next notification without circular reference
const continuePrimaryTimerAtom = atom(null, (get, set, _: null) => {
  const current = get(currentPrimarySnackbarAtom);
  manageTimer(
    get,
    set,
    primaryTimerAtom,
    removePrimarySnackbarAtom,
    current || null,
    primarySnackbarsAtom
  );
});

// Timer continuation atoms - these handle progressing to next notification without circular reference
const continueAlertTimerAtom = atom(null, (get, set, _: null) => {
  const current = get(currentAlertSnackbarAtom);
  manageTimer(
    get,
    set,
    alertTimerAtom,
    removeAlertSnackbarAtom,
    current || null,
    alertSnackbarsAtom
  );
});

// Enhanced remove atoms with timer management
export const removePrimarySnackbarAtom = atom(null, (get, set, id: string) => {
  const currentBefore = get(currentPrimarySnackbarAtom);

  // Remove from queue
  set(basePrimaryRemoveAtom, id);

  // Check if current changed and manage timer
  const currentAfter = get(currentPrimarySnackbarAtom);
  if (currentBefore?.id !== currentAfter?.id) {
    set(continuePrimaryTimerAtom, null);
  }
});

export const removeAlertSnackbarAtom = atom(null, (get, set, id: string) => {
  const currentBefore = get(currentAlertSnackbarAtom);
  set(baseAlertRemoveAtom, id);

  // Check if current changed and manage timer
  const currentAfter = get(currentAlertSnackbarAtom);
  if (currentBefore?.id !== currentAfter?.id) {
    set(continueAlertTimerAtom, null);
  }
});

type RemoveSnackbarAtom = typeof removePrimarySnackbarAtom | typeof removeAlertSnackbarAtom;
